import axios from 'axios';
import authService from './auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      authService.removeToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Public IP types
export interface PublicIP {
  id: number;
  office_id: number;
  cidr: string;
  isp: string;
  description?: string;
  created_at: string;
}

export interface PublicIPCreate {
  office_id: number;
  cidr: string;
  isp: string;
  description?: string;
}

export interface PublicIPUpdate {
  cidr?: string;
  isp?: string;
  description?: string;
}

// Office types
export interface Office {
  id: number;
  building: string;
  floor: string;
  description?: string;
  created_at: string;
  ip_ranges: IPRange[];
  public_ips: PublicIP[];
}

export interface OfficeCreate {
  building: string;
  floor: string;
  description?: string;
}

// IP Range types
export interface IPRange {
  id: number;
  office_id: number;
  network: string;
  subnet_mask: number;
  dhcp_start?: string;
  dhcp_end?: string;
  static_start?: string;
  static_end?: string;
  vlan_id?: number;
  description?: string;
  created_at: string;
  static_ips: StaticIP[];
}

export interface IPRangeCreate {
  office_id: number;
  network: string;
  subnet_mask: number;
  dhcp_start?: string;
  dhcp_end?: string;
  static_start?: string;
  static_end?: string;
  vlan_id?: number;
  description?: string;
}

// Static IP types
export interface StaticIP {
  id: number;
  ip_range_id: number;
  ip_address: string;
  device_name?: string;
  device_type?: string;
  mac_address?: string;
  description?: string;
  is_active: boolean;
  assigned_date: string;
  last_updated: string;
}

export interface StaticIPCreate {
  ip_range_id: number;
  ip_address: string;
  device_name?: string;
  device_type?: string;
  mac_address?: string;
  description?: string;
}

export interface StaticIPUpdate {
  device_name?: string;
  device_type?: string;
  mac_address?: string;
  description?: string;
  is_active?: boolean;
}

// Office API calls
export const officeApi = {
  getAll: () => api.get<Office[]>('/offices'),
  getById: (id: number) => api.get<Office>(`/offices/${id}`),
  create: (data: OfficeCreate) => api.post<Office>('/offices', data),
  update: (id: number, data: Partial<OfficeCreate>) => api.put<Office>(`/offices/${id}`, data),
  delete: (id: number) => api.delete(`/offices/${id}`),
};

// IP Range API calls
export const ipRangeApi = {
  getAll: (officeId?: number) => {
    const params = officeId ? { office_id: officeId } : {};
    return api.get<IPRange[]>('/ip-ranges', { params });
  },
  getById: (id: number) => api.get<IPRange>(`/ip-ranges/${id}`),
  create: (data: IPRangeCreate) => api.post<IPRange>('/ip-ranges', data),
  update: (id: number, data: Partial<IPRangeCreate>) => api.put<IPRange>(`/ip-ranges/${id}`, data),
  delete: (id: number) => api.delete(`/ip-ranges/${id}`),
};

// Static IP API calls
export const staticIpApi = {
  getAll: (ipRangeId?: number, isActive?: boolean) => {
    const params: any = {};
    if (ipRangeId !== undefined) params.ip_range_id = ipRangeId;
    if (isActive !== undefined) params.is_active = isActive;
    return api.get<StaticIP[]>('/static-ips', { params });
  },
  getById: (id: number) => api.get<StaticIP>(`/static-ips/${id}`),
  create: (data: StaticIPCreate) => api.post<StaticIP>('/static-ips', data),
  update: (id: number, data: StaticIPUpdate) => api.put<StaticIP>(`/static-ips/${id}`, data),
  delete: (id: number) => api.delete(`/static-ips/${id}`),
};

// Public IP API calls
export const publicIpApi = {
  getAll: (officeId?: number) => {
    const params = officeId ? { office_id: officeId } : {};
    return api.get<PublicIP[]>('/public-ips', { params });
  },
  getById: (id: number) => api.get<PublicIP>(`/public-ips/${id}`),
  create: (data: PublicIPCreate) => api.post<PublicIP>('/public-ips', data),
  update: (id: number, data: PublicIPUpdate) => api.put<PublicIP>(`/public-ips/${id}`, data),
  delete: (id: number) => api.delete(`/public-ips/${id}`),
};

// DHCP IP types
export interface DhcpIP {
  id: number;
  hostname?: string;
  ip_address: string;
  mac_address?: string;
  device_type?: string;
  manufacturer?: string;
  model?: string;
  os_version?: string;
  location?: string;
  description?: string;
  last_seen?: string;
}

export const dhcpIpApi = {
  getAll: (subnetNetwork?: string, subnetMask?: number) =>
    api.get<DhcpIP[]>('/dhcp-ips', { 
      params: { 
        subnet_network: subnetNetwork, 
        subnet_mask: subnetMask 
      } 
    }),
  search: (query: string) =>
    api.get<DhcpIP[]>('/dhcp-ips/search', { params: { query } }),
  release: (macAddress: string, ipAddress?: string) =>
    api.delete('/dhcp-ips/release', { 
      params: { 
        mac_address: macAddress,
        ip_address: ipAddress 
      } 
    }),
};

export default api;